import miaPlatformDataService from '../services/MiaPlatformDataService';
import * as vfsClient from './vfsClient';

// Creates a fresh, vfs, vfs-backed project with a default (no-prompt-yet)
// name, mirroring the real "chat -> project" creation sequence used by
// App.jsx's handleStartChatFromHome `mode === 'code'` branch
// (miaPlatformDataService.createProject -> vfsClient.createProject/
// markProjectVfsBacked) but without the AI project-naming step or the
// multi-step creation modal — this is used for three "quick create" entry
// points: landing on bare /code with no previously-selected project,
// clicking "New" to start a fresh (chat, project) pair, and
// ProjectPickerModal's "Recreate this project" action (passing the original
// project's own name via `projectNameOverride` instead of a generated
// "Untitled Project <timestamp>" one). Reused by all three so they can't
// drift apart. Lives in its own module (not App.jsx) so ProjectPickerModal.jsx
// can import it without a circular App.jsx <-> ProjectPickerModal.jsx dependency.

// `registerDefaultService: false` (Open in Flow from Console Next): the project's services come from an import, so no
// placeholder 'services/app' is registered.
export async function createQuickVfsProject(tenantId, projectNameOverride = null, conversationId = null, { registerDefaultService = true } = {}) {
    const stamp = new Date().toLocaleString('en-GB', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
    }).replace(',', '');
    const projectName = projectNameOverride || `Untitled Project ${stamp}`;

    // No template lookup: every vfs-backed project starts empty regardless
    // (the agent scaffolds it via vfs_create_project/vfs_write_file, never
    // from a Console template) — the project-blueprint call this used to
    // make (getProjectBlueprint) always 403'd in practice and its result was
    // never actually used for anything, just a noisy, pointless request on
    // every single project creation. Removed outright per explicit request.
    const result = await miaPlatformDataService.createProject({
        tenantId,
        projectName,
        projectDescription: '',
        templateId: null
    });

    const projectId = result.project?._id || result.project?.id;
    if (!projectId) {
        throw new Error('Project creation did not return a project id');
    }

    // Provision vfs-service storage (git-free), same as the real flow.
    await vfsClient.createProject(projectId);
    vfsClient.markProjectVfsBacked(projectId);

    // A project must never exist without `.mia/project.json`, even
    // transiently — write a default one synchronously as part of
    // provisioning, right here, not left for the agent's own
    // `vfs_set_project_metadata` call to create for the first time
    // (possibly several turns later, or never, if the user never chats).
    // The agent later UPDATES this same file once it understands the app's
    // real purpose; it must never be the one that first creates it.
    //
    // `conversationId` is threaded through from the caller (generated
    // BEFORE this function runs, then reused in the navigate() URL after)
    // so this placeholder file is never unbound from its conversation even
    // if the agent's own vfs_set_project_metadata call is delayed or
    // skipped — adk-be-app's vfs_ensure_project also self-heals this field
    // on every turn as a second line of defense, but writing it here at the
    // source closes the gap immediately rather than relying solely on that.
    await vfsClient.writeDefaultProjectMetadata(projectId, projectName, '', conversationId);

    // Register a default service immediately so the project never shows the
    // empty "No services yet" state — 'services/app' is the current
    // convention for "the project's one default service" (see Canvas.jsx's
    // loadConfiguration comment): every real service lives under its own
    // subfolder of `services/`, never at the workspace root (that root is
    // reserved for `.mia/project.json` and other project-level files). This
    // is registered here purely as a local pointer (localStorage) so the FE
    // immediately has a real vfsServiceName to scope the first chat turn's
    // `x-mia-vfs-service` header to — no vfs-service write happens yet, the
    // folder becomes real the moment the agent (or the user) writes the
    // first file under it. #78 (chat-goal renaming) relabels this once the
    // conversation reveals what's actually being built.
    if (registerDefaultService) {
        vfsClient.registerVfsService(projectId, 'services/app');
    }

    return { projectId, name: result.project?.name || projectName };
}
